import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Everyday Essentials",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`,
    "description": "A curated collection of everyday essentials, home finds, beauty products, travel must-haves, and simple favorites I personally use and recommend",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <SEOHead
        title="Everyday Essentials I Actually Use & Recommend"
        description="A curated collection of everyday essentials, home finds, beauty products, travel must-haves, and simple favorites I personally use and recommend. Simple, useful finds — no spam, no hype."
        keywords="everyday essentials, home finds, beauty essentials, travel must-haves, product recommendations, amazon finds"
        canonicalPath="/"
        schemaData={schemaData}
      />
      <div className="min-h-screen w-full animated-gradient flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Profile and Text */}
            <div className="flex flex-col items-center lg:items-end text-center lg:text-right space-y-6">
              {/* Profile Image */}
              <div className="w-64 h-64 rounded-full overflow-hidden border-8 border-white/90 shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/918694c0b33b18123a4b53c7a1500734/3e4fe73e48c80acaec1c0e5585c59416.png" 
                  alt="Profile" 
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Main Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                Things I Actually<br />Use & Recommend
              </h1>

              {/* Subtext */}
              <div className="text-white/95 text-lg md:text-xl space-y-2">
                <p>Simple, useful finds — no spam, no hype. no onlyfans</p>
                <p>just everyday essentials I genuinely like. If you enjoy the page, support is always appreciated 🤍</p>
              </div>
            </div>

            {/* Right Column - Buttons */}
            <div className="flex flex-col items-center lg:items-start space-y-4 w-full max-w-md mx-auto lg:mx-0">
              <Link 
                to="/home-finds"
                className="w-full bg-gradient-to-r from-[#4a8b8f] to-[#5a9b9f] hover:from-[#3a7b7f] hover:to-[#4a8b8f] text-white font-semibold text-lg md:text-xl py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-between group whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="text-2xl">🛍</span>
                  <span>Home Finds</span>
                </span>
                <i className="ri-arrow-right-line text-2xl opacity-80 group-hover:opacity-100 transition-opacity"></i>
              </Link>

              <Link 
                to="/beauty-essentials"
                className="w-full bg-gradient-to-r from-[#7b6ba8] to-[#8b7bb8] hover:from-[#6b5b98] hover:to-[#7b6ba8] text-white font-semibold text-lg md:text-xl py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-between group whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="text-2xl">💄</span>
                  <span>Beauty Essentials</span>
                </span>
                <i className="ri-arrow-right-line text-2xl opacity-80 group-hover:opacity-100 transition-opacity"></i>
              </Link>

              <Link 
                to="/travel-must-haves"
                className="w-full bg-gradient-to-r from-[#d66b9f] to-[#e67baf] hover:from-[#c65b8f] hover:to-[#d66b9f] text-white font-semibold text-lg md:text-xl py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-between group whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="text-2xl">🧳</span>
                  <span>Travel Must-Haves</span>
                </span>
                <i className="ri-arrow-right-line text-2xl opacity-80 group-hover:opacity-100 transition-opacity"></i>
              </Link>

              <Link 
                to="/for-the-men"
                className="w-full bg-gradient-to-r from-[#8b9b6f] to-[#9bab7f] hover:from-[#7b8b5f] hover:to-[#8b9b6f] text-white font-semibold text-lg md:text-xl py-5 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-between group whitespace-nowrap cursor-pointer"
              >
                <span className="flex items-center gap-3">
                  <span className="text-2xl">🫵</span>
                  <span>For the Men</span>
                </span>
                <i className="ri-arrow-right-line text-2xl opacity-80 group-hover:opacity-100 transition-opacity"></i>
              </Link>
            </div>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center items-center gap-6 mt-16">
            <a 
              href="https://ko-fi.com/hiitsaria" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer kofi-shimmer"
              aria-label="Ko-fi"
            >
              <i className="ri-cup-fill text-3xl text-white"></i>
            </a>
            <a 
              href="https://www.instagram.com/hiitsaria18/" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer"
              aria-label="Instagram"
            >
              <i className="ri-instagram-fill text-3xl text-white"></i>
            </a>
            <a 
              href="https://www.tiktok.com/@hiitsaria1?is_from_webapp=1&sender_device=pc" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer"
              aria-label="TikTok"
            >
              <i className="ri-tiktok-fill text-3xl text-white"></i>
            </a>
            <a 
              href="https://pin.it/2XYqNKobE" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-16 h-16 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-300 hover:scale-110 cursor-pointer"
              aria-label="Pinterest"
            >
              <i className="ri-pinterest-fill text-3xl text-white"></i>
            </a>
          </div>

          {/* Footer Text */}
          <div className="text-center mt-12 space-y-4">
            <p className="text-white/90 text-sm md:text-base">
              As an Amazon Associate, I earn from qualifying purchases support the hustle 💋
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
